import { useNavigate } from 'react-router-dom'

import { Box, Grid } from '@factorialco/deprecated-design-system'
import { Download } from '@factorialco/f0-react/icons/app'
import {
  BenefitsBenefitAvailabilityFragment,
  BenefitsCardBenefitListHealthcarePlanFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import {
  BenefitsCardBenefit,
  type BenefitsCardBenefitActions,
} from 'modules/benefits/components/BenefitsCardBenefit/BenefitsCardBenefit'
import BenefitModals from 'modules/benefits/components/benefitModals/BenefitModals'
import { openModal } from 'modules/benefits/components/benefitModals/actions'
import { useBenefitModals } from 'modules/benefits/components/benefitModals/useBenefitModals'
import {
  BenefitListing,
  BenefitSetupStatus,
  getBenefitsList,
} from 'modules/benefits/lib/benefitListings'

interface EmployeeBenefitsListProps {
  healthcarePlans: BenefitsCardBenefitListHealthcarePlanFragment[] | undefined
  healthcareEnrollmentStatus: BenefitSetupStatus | undefined
  featureAvailabilities: BenefitsBenefitAvailabilityFragment[] | undefined
  enableEnrollment: boolean
}

const EmployeeBenefitsList = ({
  healthcarePlans,
  healthcareEnrollmentStatus: healthcareEnrollment,
  featureAvailabilities,
  enableEnrollment,
}: EmployeeBenefitsListProps) => {
  const { employee } = useCurrentSession()
  const navigate = useNavigate()

  const { dispatch } = useBenefitModals()

  const market = employee?.legalEntity?.country

  const allBenefits = market ? getBenefitsList(market, featureAvailabilities) : []
  const benefits = allBenefits
    .filter((benefit) => {
      if (benefit.availability !== 'active') {
        return false
      }
      if (benefit.id === 'health_insurance_es') {
        return healthcarePlans && healthcarePlans.length > 0
      }
      return true
    })
    .map((benefit) => {
      if (benefit.id !== 'health_insurance_es') {
        return benefit
      }
      return {
        ...benefit,
        // Hide the learn more URL from employees, the policy document is enough
        learnMoreUrl: undefined,
      }
    })

  /**
   * Determine what actions to show for a benefit.
   *
   * Right now we are generating this statically, but it will soon depend on
   * backend state.
   */
  function actionsForBenefit(benefit: BenefitListing): BenefitsCardBenefitActions | undefined {
    switch (benefit.id) {
      case 'childcare_es':
        return {
          primaryAction: {
            label: i18n.t('benefits.homepage.benefit.request'),
            onClick: () => dispatch(openModal('childcare_request')),
          },
        }
      case 'health_insurance_es':
        if (benefit.availability !== 'active' || !healthcarePlans?.length) {
          return undefined
        }
        return {
          primaryAction: enableEnrollment
            ? {
                label: i18n.t('benefits.actions.enroll_now'),
                onClick: () => dispatch(openModal('healthcare_plan_enrollment')),
              }
            : undefined,
          secondaryAction: {
            label: i18n.t('benefits.actions.download_coverage'),
            icon: Download,
            onClick: () => window.open(healthcarePlans?.[0]?.planDocument?.url ?? '', '_blank'),
          },
        }

      case 'wellness_es':
        if (benefit.availability !== 'active') {
          return undefined
        }
        return {
          primaryAction: {
            label: i18n.t('benefits.homepage.benefit.plan_details'),
            onClick: () => dispatch(openModal('wellness_information')),
          },
        }
      case 'trainings_es':
        if (benefit.availability !== 'active') {
          return undefined
        }

        return {
          primaryAction: {
            label: i18n.t('benefits.homepage.benefit.request'),
            onClick: () => dispatch(openModal('training_request')),
          },
        }
      case 'perks_es':
        if (benefit.availability !== 'active') {
          return undefined
        }

        return {
          primaryAction: {
            label: i18n.t('benefits.actions.view_catalogue'),
            onClick: () => {
              // Navigate to perks catalogue sub-page
              navigate('/my-benefits/benefits/perks')
            },
          },
        }
    }
  }

  const setupStatus = (benefit: BenefitListing): BenefitSetupStatus | undefined => {
    if (benefit.id !== 'health_insurance_es') {
      return undefined
    }
    return healthcareEnrollment
  }

  return (
    <Box gap="s12" flexDirection="row" flexWrap="wrap">
      <Grid variant="fixed">
        {benefits.map((benefit) => {
          return (
            <Box
              key={benefit.id}
              gridColSpan={{ xxs: 12, xs: 12, s: 12, m: 4, l: 4, xl: 3, xxl: 3 }}
            >
              <BenefitsCardBenefit
                benefitListing={benefit}
                setupStatus={setupStatus(benefit)}
                showTag
                actions={actionsForBenefit(benefit)}
              />
            </Box>
          )
        })}
      </Grid>

      <BenefitModals />
    </Box>
  )
}

export { EmployeeBenefitsList }
